import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaRegCalendarAlt } from "react-icons/fa";
import { useFavorites } from "./FavoritesContext";
import { api } from "./session";
import { fmtLongDate, eventEmoji } from "./eventsApi";
import "./SavedEventsCard.css";

/* =========================================================
   SavedEventsCard   "Saved events" section for the profile.
   Drop it next to Saved places:

     import SavedEventsCard from "./SavedEventsCard";
     …
     <SavedEventsCard />

   Renders nothing when signed out (the profile's own guest
   state already covers that). Uses the ghfav-* styles that
   Favorites.css provides on the profile page.
   ========================================================= */

export default function SavedEventsCard() {
  const { user, showToast } = useFavorites();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!user) {
      setEvents([]);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError("");

    api
      .get("/api/events/favorites")
      .then((response) => {
        if (alive) setEvents(response?.data?.events || []);
      })
      .catch(() => {
        if (alive) setError("Couldn't load saved events.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [user, reloadKey]);

  if (!user) return null;

  const unsave = async (ev) => {
    setEvents((prev) => prev.filter((item) => item.id !== ev.id));
    try {
      await api.post("/api/events/favorites/toggle", { eventId: ev.id });
      showToast("Removed from saved");
    } catch {
      setReloadKey((n) => n + 1);
      showToast("Couldn't update saved events");
    }
  };

  return (
    <section className="ghfav-card sec-card">
      <div className="ghfav-card__head">
        <h2>
          <FaRegCalendarAlt aria-hidden="true" /> Saved events
        </h2>
        {events.length > 0 && (
          <span className="ghfav-count">{events.length}</span>
        )}
      </div>

      {loading && <p className="ghfav-muted">Loading saved events…</p>}

      {!loading && error && (
        <p className="ghfav-muted">
          {error}{" "}
          <button
            type="button"
            className="ghfav-retry"
            onClick={() => setReloadKey((n) => n + 1)}
          >
            Try again
          </button>
        </p>
      )}

      {!loading && !error && events.length === 0 && (
        <p className="ghfav-muted">
          No saved events yet   tap the ♥ on any community event.
        </p>
      )}

      {!loading && !error && events.length > 0 && (
        <ul className="ghfav-list">
          {events.map((ev) => (
            <li key={ev.id} className="ghfav-item">
              <Link
                className="sec-link"
                to={`/communities/${String(ev.community_slug || "")
                  .toLowerCase()
                  .trim()}?tab=events&event=${ev.id}`}
              >
                <span className="sec-thumb" aria-hidden="true">
                  {ev.image_urls?.[0] ? (
                    <img src={ev.image_urls[0]} alt="" loading="lazy" />
                  ) : (
                    <span>{ev.icon || eventEmoji(ev)}</span>
                  )}
                </span>
                <span className="ghfav-item__body">
                  <span className="ghfav-item__name">{ev.title}</span>
                  <span className="ghfav-item__meta">
                    {fmtLongDate(ev.event_date)}
                    {ev.venue_name ? ` · ${ev.venue_name}` : ""}
                  </span>
                </span>
              </Link>

              <button
                type="button"
                className="ghfav-btn ghfav-btn--round is-saved sec-heart"
                onClick={() => unsave(ev)}
                aria-label={`Remove ${ev.title} from saved`}
              >
                <FaHeart />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
